import isEmpty from 'lodash/isEmpty'

/**
 * Generates serial numbers based on a pattern.
 *
 * Pattern syntax:
 * - 'X' represents incrementing digits
 * - '0' before 'X' represents zero-padding
 * - '\X' represents a literal X character (escaped)
 *
 * @param pattern - The pattern string (e.g., 'PMX_00XX' or 'ABC-XXX')
 * @param count - The number of serial numbers to generate
 * @returns An array of generated serial numbers
 *
 * @example
 * generateSerialNumbers('PMX_00XX', 3)
 * // Returns: ['PMX_0001', 'PMX_0002', 'PMX_0003']
 *
 * @example
 * generateSerialNumbers('ABC-XXX', 2)
 * // Returns: ['ABC-001', 'ABC-002']
 *
 * @example
 * generateSerialNumbers('DMS_MS_12\\X78\\XXX', 2)
 * // Returns: ['DMS_MS_12X78X01', 'DMS_MS_12X78X02']
 */
export function generateSerialNumbers(pattern: string, count: number): string[] {
  // Use a placeholder for escaped X's that won't conflict with normal patterns
  const ESCAPED_X_PLACEHOLDER = '\u0000ESCAPED_X\u0000'

  // Replace escaped X's (\X) with placeholder
  const processedPattern = pattern.replace(/\\X/g, ESCAPED_X_PLACEHOLDER)

  // Find all consecutive groups of X's in the pattern (excluding escaped ones)
  const xMatches = Array.from(processedPattern.matchAll(/X+/g))
  if (isEmpty(xMatches)) {
    throw new Error('Pattern must contain at least one X')
  }

  // Find the maximum length among all X groups
  const maxLength = Math.max(...xMatches.map((match) => match[0].length))
  // Get all matches with the maximum length
  const longestMatches = xMatches.filter((match) => match[0].length === maxLength)
  // Use the LAST occurrence of the longest X group as the placeholder for numbers
  const longestMatch = longestMatches[longestMatches.length - 1]
  const xPosition = longestMatch.index
  const xCount = longestMatch[0].length

  // Extract prefix (everything before the X group)
  const prefix = processedPattern.substring(0, xPosition)

  // Extract suffix (everything after the X group)
  const suffix = processedPattern.substring(xPosition + xCount)

  // Count total digits including leading zeros before X's
  const leadingZeros = prefix.match(/0*$/)?.[0].length || 0
  const totalDigits = leadingZeros + xCount

  // Generate serial numbers
  const serialNumbers: string[] = []
  for (let i = 1; i <= count; i++) {
    const numberStr = i.toString().padStart(totalDigits, '0')
    const prefixWithoutTrailingZeros = prefix.substring(0, prefix.length - leadingZeros)
    const serialNumber = `${prefixWithoutTrailingZeros}${numberStr}${suffix}`
    // Replace placeholder back with literal X
    serialNumbers.push(serialNumber.replace(new RegExp(ESCAPED_X_PLACEHOLDER, 'g'), 'X'))
  }

  return serialNumbers
}
